import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { Observable, catchError, finalize, switchMap, throwError } from 'rxjs';

import { environment } from '../../environments/environment';
import { StorageService } from '../services/storage.service';
import { LoaderService } from './loader.service';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private isRefreshing = false;

  constructor(
    private readonly loaderService: LoaderService,
    private readonly msalService: MsalService,
    private readonly router: Router,
    private readonly storageService: StorageService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isApiRequest = request.url.startsWith('/api') || request.url.startsWith('/ext');
    // The public customer page has no MSAL session; never bounce it to /login.
    const isPublicPage = this.router.url.startsWith('/public/');

    if (isApiRequest) {
      this.loaderService.show();
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          error.status === 401 &&
          isApiRequest &&
          !isPublicPage &&
          !this.isRefreshing &&
          this.isOwnSessionFailure(error)
        ) {
          return this.refreshAndRetry(request, next);
        }
        return throwError(() => error);
      }),
      finalize(() => {
        if (isApiRequest) {
          this.loaderService.hide();
        }
      })
    );
  }

  // Only the caller's own MSAL session should trigger a refresh + /login bounce.
  // A 401 that reports an upstream (S2) auth failure says nothing about the
  // user's session, and signing them out hides the real error.
  private isOwnSessionFailure(error: HttpErrorResponse): boolean {
    const code = (error.error as { error?: string } | null)?.error;
    return code !== 'upstream_authentication_error' && code !== 'upstream_integration_error';
  }

  private refreshAndRetry(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    this.isRefreshing = true;

    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      this.isRefreshing = false;
      this.storageService.remove('access_token');
      this.router.navigate(['/login'], { queryParams: { session_expired: '1' } });
      return throwError(() => new Error('No active Microsoft account'));
    }

    return this.msalService.acquireTokenSilent({
      account,
      scopes: environment.apiScopes
    }).pipe(
      switchMap(result => {
        this.isRefreshing = false;
        this.storageService.set('access_token', result.accessToken);
        const retried = request.clone({
          setHeaders: { Authorization: `Bearer ${result.accessToken}` }
        });
        return next.handle(retried);
      }),
      catchError(err => {
        this.isRefreshing = false;
        this.storageService.remove('access_token');
        this.router.navigate(['/login'], { queryParams: { session_expired: '1' } });
        return throwError(() => err);
      })
    );
  }
}
